import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  SchoolMember,
  SchoolMemberType,
} from '../../models/member.school.model';
import { MembershipStatus } from '../../models/membership.status';
import {
  schoolMemberAdapter,
  SchoolMemberState,
} from '../reducers/member.school.reducer';

export const getSchoolMemberState =
  createFeatureSelector<SchoolMemberState>('schoolMembers');
export const getIds = (state: SchoolMemberState) => state.ids;
export const memberStatsLoading = (state: SchoolMemberState) =>
  state.memberStatsLoading;

export const {
  selectIds: getSchoolMemberIds,
  selectEntities: getSchoolMemberEntities,
  selectAll: getAllSchoolMembers,
  selectTotal: getTotalSchoolMembers,
} = schoolMemberAdapter.getSelectors(getSchoolMemberState);

export const loading = createSelector(
  getSchoolMemberState,
  (schoolMemberState: SchoolMemberState) => schoolMemberState.loading,
);

export const inProgress = createSelector(
  getSchoolMemberState,
  (schoolMemberState: SchoolMemberState) => schoolMemberState.inProgress,
);

export const getSchoolMemberById = (
  memberId: string | null,
  groupId: string | null,
) =>
  createSelector(getAllSchoolMembers, (allItems) => {
    if (!memberId || !groupId) return undefined;
    if (allItems) {
      return allItems.find((item) => {
        return item.memberId === memberId && item.groupId === groupId;
      });
    } else {
      return null;
    }
  });

export const getStudentsByParentId = (
  parentId: string | null,
  groupId: string | null,
  searchTerm?: string,
) =>
  createSelector(getAllSchoolMembers, (allItems) => {
    if (!parentId || !groupId) return [];
    if (allItems) {
      return allItems.filter((item) => {
        return (
          item.parentId === parentId &&
          item.groupId === groupId &&
          (searchTerm
            ? item.name?.toLowerCase().includes(searchTerm.toLowerCase())
            : true)
        );
      });
    } else {
      return [];
    }
  });

export const getSchoolMembersByGroupId = (groupId: string) =>
  createSelector(getAllSchoolMembers, (allItems) => {
    // if (allItems) {
    return allItems
      .filter((item) => {
        return item.groupId === groupId;
      })
      .sort(sortDescendingByModifiedDate);
  });

export const getMembersIdsByGroup = (groupId: string) =>
  createSelector(getAllSchoolMembers, (allItems) => {
    return allItems
      .filter((item) => item.groupId === groupId)
      .map((item) => item.memberId);
  });

export const getGroupMembersByType = ({
  memberType,
  groupId,
  status,
  filter,
}: {
  memberType: SchoolMemberType[];
  groupId: string | null;
  status: MembershipStatus[];
  filter?: string;
}) =>
  createSelector(getAllSchoolMembers, (allItems) => {
    if (!groupId) return [];
    return allItems
      .filter((item) => {
        return (
          item.groupId === groupId &&
          status.includes(item.status!) &&
          item.memberType?.some((type) => memberType?.includes(type)) &&
          (!filter || item.name?.toLowerCase().includes(filter.toLowerCase()))
        );
      })
      .sort(sortDescendingByModifiedDate);
  });

export const getStudentsByGrade = ({
  gradeId,
  groupId,
  academicYear,
}: {
  gradeId: string | null;
  groupId: string | null;
  academicYear: number | null | undefined;
}) =>
  createSelector(getAllSchoolMembers, (allItems) => {
    if (!gradeId || !groupId || !academicYear) return [];
    return allItems
      .filter((item) => {
        return (
          item.groupId === groupId &&
          item.memberType?.some((type) =>
            SchoolMemberType.student.includes(type),
          ) &&
          item.gradeId === gradeId &&
          item.academicYear === academicYear
        );
      })
      .sort(sortDescendingByModifiedDate);
  });

export const getGroupMembersByStatus = (
  groupId: string | null,
  status: MembershipStatus[],
) =>
  createSelector(getAllSchoolMembers, (allItems) => {
    if (!groupId) return [];
    return allItems
      .filter((item) => {
        return item.groupId === groupId && status?.includes(item.status!);
      })
      .sort(sortDescendingByModifiedDate);
  });

// Docs written with serverTimestamp() have no `modified` until the server
// confirms; treat those as the newest.
function modifiedMillis(m: SchoolMember): number {
  return m.modified?.toMillis?.() ?? Number.MAX_SAFE_INTEGER;
}

function sortDescendingByModifiedDate(
  a: SchoolMember,
  b: SchoolMember,
): number {
  return modifiedMillis(b) - modifiedMillis(a);
}
